import React, { FC, useEffect, useRef, useState } from "react"
import { Tooltip } from "react-tooltip"

// Utils
import { isTouchDevice } from "@utils"

// Shared Components
import { ICON_TYPES } from "@components/Icon/Icon"

// Store
import { QUESTION_LIST_VARIANTS } from "../../../../_store/constants"
import { Question } from "../../../../_store/types"

// Sub-components
import QuestionCardActionButton from "./_components/QuestionCardActionButton/QuestionCardActionButton"
import QuestionCardInput from "./_components/QuestionCardInput/QuestionCardInput"

// Styled Components
import {
  QuestionCardDIV,
  QuestionCardActionsDIV,
} from "./QuestionCard.styles"

const HAS_SHOWN_ADDED_TO_INTERVIEW_HELPER_LS_KEY = "hs--interviewBuilder--hasShownAddedToInterviewHelper"
const SHOW_ADDED_TO_INTERVIEW_HELPER_MS = 4000

type QuestionCardProps = Question & {
  index: number,
  isCollapsed?: boolean,
  isDragging?: boolean,
  isEditing?: boolean,
  isInInterview?: boolean,
  isMobile?: boolean,
  isPlaceholder?: boolean,
  onClickAdd?: () => void,
  onClickEdit?: () => void,
  onClickPermanentlyDelete: () => void,
  onClickRemove?: () => void,
  onClickReset?: () => void,
  onFinishedEditing?: (text: string) => void,
  onStartDragging?: (clientRect: DOMRect) => void,
  showEditPrompt?: boolean,
  variant: QUESTION_LIST_VARIANTS,
}

const QuestionCard: FC<QuestionCardProps> = ({
  index,
  isCollapsed,
  isDragging,
  isEditing,
  isInInterview,
  isMobile,
  isPlaceholder,
  onClickAdd,
  onClickEdit,
  onClickPermanentlyDelete,
  onClickRemove,
  onClickReset,
  onFinishedEditing,
  onStartDragging,
  showEditPrompt,
  textDefault,
  textEdited,
  variant,
}) => {
  const elRef = useRef<HTMLDivElement | null>(null)
  const isLibrary = variant === QUESTION_LIST_VARIANTS.LIBRARY
  const [isRemoving, setIsRemoving] = useState<boolean>(false)
  const [longPressTimestamp, setLongPressTimestamp] = useState<number>(0)
  const [textCurrent, setTextCurrent] = useState<string>(textEdited || textDefault)
  const [showAddedToInterviewHelper, setShowAddedToInterviewHelper] = useState<boolean>(false)

  useEffect(() => {
    setTextCurrent(textEdited || textDefault)

    // If this Card has just been added, put it into Edit mode automatically
    if (!textDefault && !textEdited && onClickEdit) {
      onClickEdit()
    }
  }, [textDefault, textEdited])

  const handleClickRemove = () => {
    setIsRemoving(true)
    // Wait for the animation
    if (onClickRemove) setTimeout(onClickRemove, 300)
  }

  const handleClickPermanentlyDelete = () => {
    setIsRemoving(true)
    // Wait for the animation
    if (onClickPermanentlyDelete) setTimeout(onClickPermanentlyDelete, 300)
  }

  const handleStartDragging = (): void => {
    if (elRef.current && onStartDragging) {
      const clientRect = elRef.current.getBoundingClientRect()
      onStartDragging(clientRect)
    }
  }

  // Disable the context menu while a long press is happening
  const handleContextMenuContent = (e: React.TouchEvent<HTMLSpanElement> | React.MouseEvent<HTMLSpanElement>) => {
    if (longPressTimestamp > 0) e.preventDefault()
  }

  // Log the beginning of a (potential) long press
  const handleTouchStartContent = (e: React.TouchEvent<HTMLSpanElement>) => {
    if ((e.target as HTMLElement).className.includes("QuestionCardContent")) {
      // e.preventDefault()
      setLongPressTimestamp(e.timeStamp)
    }
  }

  const handleTouchEndContent = (e: React.TouchEvent<HTMLSpanElement>) => {
    // If it is a long press, prevent default, and trigger edit mode
    if (
      onClickEdit &&
      // Not a long press if the user is scrolling (cancellable event)
      e.cancelable &&
      // Not a long press if the target is not .QuestionCardContent
      (e.target as HTMLElement).className.includes("QuestionCardContent") &&
      // Not currently dragging
      !isDragging &&
      // Not a long press if there's no timestamp
      longPressTimestamp &&
      // Make sure the timestamp is over 300ms old and less than 3s old
      e.timeStamp - longPressTimestamp >= 300 && e.timeStamp - longPressTimestamp <= 3000
    ) {
      e.preventDefault()
      onClickEdit()
    }

    if (!!window.navigator.vibrate) window.navigator.vibrate(20)

    // Always reset the long press timer
    setLongPressTimestamp(0)
  }

  const handleDoubleClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
    if (onClickEdit) onClickEdit()
  }

  const handleAddToInterview = () => {
    const hasShownAddedToInterviewHelper = localStorage.getItem(HAS_SHOWN_ADDED_TO_INTERVIEW_HELPER_LS_KEY)
    if (!hasShownAddedToInterviewHelper) {
      setShowAddedToInterviewHelper(true)

      setTimeout(() => {
        setShowAddedToInterviewHelper(false)
        localStorage.setItem(HAS_SHOWN_ADDED_TO_INTERVIEW_HELPER_LS_KEY, "true")
      }, SHOW_ADDED_TO_INTERVIEW_HELPER_MS)
    }
    if (onClickAdd) onClickAdd()
  }

  return (
    <QuestionCardDIV
      data-index={index}
      data-tooltip-id={`Tooltip--card-${index}`}
      className={[
        `QuestionCard`,
        isRemoving ? "is-removing" : "",
        isEditing ? "is-editing" : "",
        isDragging ? "is-dragging" : "",
        isPlaceholder ? "is-placeholder" : "",
        (isMobile && showEditPrompt) ? "show-edit-prompt" : "",
      ].filter(v => v).join(" ")}
      ref={elRef}
    >
      {!isCollapsed && isMobile && showEditPrompt && (
        <Tooltip
          id={`Tooltip--card-${index}`}
          isOpen
        >
          <p>
            {
              isTouchDevice() ?
                `Press and hold to edit a question` :
                `Double-click to edit a question`
            }
          </p>
        </Tooltip>
      )}
      {!isCollapsed && isMobile && showAddedToInterviewHelper && (
        <Tooltip
          id={`Tooltip--card-${index}`}
          isOpen
        >
          <p>Selected questions are added to Your&nbsp;Interview below</p>
        </Tooltip>
      )}
      <QuestionCardActionsDIV className="actions actions-left">
        {/* Drag Handle */}
        {!(isMobile && isLibrary) && (
          <QuestionCardActionButton
            className="drag-handle"
            iconType={ICON_TYPES["move"]}
            id="drag-handle"
            isMobile={!!isMobile}
            tooltipText={isLibrary ? `Drag this Question to the Interview column` : `Drag this Question to change its position in the list`}
            tooltipOnRight={isLibrary}
            onMouseDown={!(isMobile && isLibrary) && !isDragging ? handleStartDragging : undefined}
          />
        )}
        {/* Mobile Checkbox Checked/Unchecked in Interview */}
        {isMobile && isLibrary && (
          <QuestionCardActionButton
            className={`mobile-checkbox ${isInInterview ? "is-in-interview" : ""}`}
            iconType={ICON_TYPES["checkmark"]}
            id="mobile-checkbox"
            isMobile={!!isMobile}
            onClick={isInInterview ? onClickRemove : handleAddToInterview}
            tooltipText={isInInterview ? "Remove from Interview" : "Add to Interview"}
            tooltipOnRight
          />
        )}
      </QuestionCardActionsDIV>
      {
        (!isEditing || (isEditing && isLibrary)) && (
          <span
            className="QuestionCardContent"
            onDoubleClick={!isLibrary && !isDragging ? handleDoubleClick : undefined}
            onContextMenu={!isLibrary && !isDragging ? handleContextMenuContent : undefined}
            onTouchStart={!isLibrary && !isDragging ? handleTouchStartContent : undefined}
            onTouchEnd={!isLibrary && !isDragging ? handleTouchEndContent : undefined}
          >
            {textEdited || textDefault}
          </span>
        )
      }
      {
        !isLibrary && isEditing && onFinishedEditing && (
          <QuestionCardInput
            textCurrent={textCurrent}
            text={textEdited || textDefault}
            onChange={(value: string) => setTextCurrent(value)}
            onFinishedEditing={onFinishedEditing}
          />
        )
      }
      <QuestionCardActionsDIV className="actions actions-right">
        {/* Interview Actions */}
        {!isDragging && !isLibrary && (
          <>
            {/* Remove/move back to Library */}
            <QuestionCardActionButton
              iconType={ICON_TYPES["close"]}
              id="close"
              isMobile={isMobile}
              onClick={handleClickRemove}
              tooltipText="Removed questions can be found again in the Question Library"
            />
            {/* Reset (if text has been edited) */}
            {!!textDefault && !!textEdited && (
              <QuestionCardActionButton
                iconType={ICON_TYPES["refresh"]}
                id="refresh"
                isMobile={isMobile}
                onClick={onClickReset}
                tooltipText="Reset Question to Original"
              />
            )}
            {/* Edit */}
            {!isEditing && (
              <QuestionCardActionButton
                iconType={ICON_TYPES["pencil"]}
                id="edit"
                isMobile={isMobile}
                onClick={onClickEdit}
                tooltipText="Edit Question"
              />
            )}
            {/* Save */}
            {isEditing && (
              <QuestionCardActionButton
                className="save-button"
                iconType={ICON_TYPES["checkmark"]}
                id="save"
                isMobile={isMobile}
                onClick={() => onFinishedEditing && onFinishedEditing(textCurrent)}
                tooltipText="Save Updated Text"
              />
            )}
          </>
        )}
        {!isDragging && isLibrary && (
          <>
            {/* Permanently delete custom question */}
            {!textDefault && !!textEdited && (
              <QuestionCardActionButton
                iconType={ICON_TYPES["close"]}
                id="mobile-close"
                isMobile={isMobile}
                onClick={handleClickPermanentlyDelete}
                tooltipText="Custom questions will be deleted permanently"
              />
            )}
            {/* Reset (if text has been edited) */}
            {!!textDefault && !!textEdited && (
              <QuestionCardActionButton
                iconType={ICON_TYPES["refresh"]}
                id="reset"
                isMobile={isMobile}
                onClick={onClickReset}
                tooltipText="Reset Question to Original"
              />
            )}
          </>
        )}
      </QuestionCardActionsDIV>
    </QuestionCardDIV >
  )
}

export default QuestionCard
